import { CUSTOM_ELEMENTS_SCHEMA, Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, take } from 'rxjs';
import {
  InstitutoPrevidenciarioAvaliacao,
  InstitutoPrevidenciarioDocumento,
} from '../../../core/models/instituto-previdenciario.model';
import { RequerimentoOnlineDetalhe } from '../../../core/models/requerimento-beneficio/requerimento-online-solicitacao.model';
import { RequerimentoOnlineSolicitacaoService } from '../../../core/services/requerimento-online-solicitacao.service';
import { ToastService } from '../../../core/services/toast.service';
import {
  DOCUMENTO_REENVIADO,
  DOCUMENTO_REJEITADO,
  REQUERIMENTO_STATUS_EM_ANALISE,
} from '../../../core/constants/requerimento-online-opcoes';
import { formatDateIsoToBr } from '../../../core/utils/date-format.util';
import { VivestModalAreaVisivelDirective } from '../../../shared/directives/modal-vivest/vivest-modal-area-visivel.directive';
import { RequerimentoStatusTagComponent } from '../../../shared/components/requerimento-status-tag/requerimento-status-tag.component';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { COMENTARIO_MAXIMO, COMENTARIO_MINIMO } from '../../instituto-online/detalhe-solicitacao/detalhe-solicitacao.component';

@Component({
  selector: 'app-detalhe-requerimento',
  imports: [ReactiveFormsModule, VivestModalAreaVisivelDirective, RequerimentoStatusTagComponent],
  templateUrl: './detalhe-requerimento.component.html',
  styleUrl: './detalhe-requerimento.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class DetalheRequerimentoComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly requerimentoOnlineSolicitacaoService = inject(RequerimentoOnlineSolicitacaoService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);

  private readonly solicitacaoSignal = signal<RequerimentoOnlineDetalhe | null>(null);
  readonly solicitacao = this.solicitacaoSignal.asReadonly();
  readonly documentos = computed(() => this.solicitacao()?.docs ?? []);
  /** Mudanças de status da mais recente para a mais antiga (data ISO compara como texto). */
  readonly historico = computed(() =>
    [...(this.solicitacao()?.historico ?? [])].sort((a, b) => (b.alteradoEm ?? '').localeCompare(a.alteradoEm ?? '')),
  );
  /**
   * Aprovar ou pedir reenvio só em "Solicitado" ou "Documento reenviado" (o participante mandou de
   * novo o que foi rejeitado); nos demais, só consulta.
   */
  readonly podeValidarDocumentos = computed(() => REQUERIMENTO_STATUS_EM_ANALISE.includes(this.solicitacao()?.status ?? ''));
  /** Documentos que o participante reenviou e aguardam nova análise. */
  readonly documentosReenviados = computed(() => this.documentos().filter((documento) => this.reenviado(documento)));
  /** "Comprovante de residência" ou "Comprovante de residência e Laudo médico pericial". */
  readonly nomesReenviados = computed(() => {
    const nomes = this.documentosReenviados().map((documento) => documento.tipoDocumento);
    return nomes.length > 1 ? `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}` : (nomes[0] ?? '');
  });
  /** Participante com um documento só para validar: os textos das ações ficam no singular. */
  readonly documentoUnico = computed(() => this.documentos().length === 1);
  readonly naoEncontrada = signal(false);
  readonly formatDate = formatDateIsoToBr;

  readonly confirmacaoAberta = signal(false);
  readonly reenvioAberto = signal(false);
  readonly comentarioMaximo = COMENTARIO_MAXIMO;

  /** Documento aberto no modal de visualização; `null` com o modal fechado. */
  readonly documentoVisualizado = signal<InstitutoPrevidenciarioDocumento | null>(null);
  /**
   * O link vem da API (ou do mock) e vai para o `src` do iframe, que exige URL confiável. Sem
   * fragmento próprio, ganha `#view=FitH`: o visualizador de PDF abre a página na largura do modal.
   */
  readonly urlVisualizacao = computed<SafeResourceUrl | null>(() => {
    const documento = this.documentoVisualizado();
    if (!documento) {
      return null;
    }
    const url = documento.link.includes('#') ? documento.link : `${documento.link}#view=FitH`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  });

  /** Pedido de reenvio: ao menos um documento (pelo nome do arquivo) e um comentário opcional. */
  readonly reenvioForm = this.fb.nonNullable.group({
    documentos: [[] as string[], Validators.required],
    comentarios: ['', [Validators.minLength(COMENTARIO_MINIMO), Validators.maxLength(COMENTARIO_MAXIMO)]],
  });

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');

    if (!id) {
      this.naoEncontrada.set(true);
      return;
    }

    this.carregarSolicitacao(id);
  }

  /** `2026-10-01T09:15:00` → `01/10/2026 às 09:15`; sem hora, só a data. */
  dataHora(iso: string): string {
    const [data, hora = ''] = iso.split(/[T ]/);
    const dataBr = formatDateIsoToBr(data);
    return hora ? `${dataBr} às ${hora.slice(0, 5)}` : dataBr;
  }

  reenviado(documento: InstitutoPrevidenciarioDocumento): boolean {
    return documento.statusAvaliacao === DOCUMENTO_REENVIADO;
  }

  rejeitado(documento: InstitutoPrevidenciarioDocumento): boolean {
    return documento.statusAvaliacao === DOCUMENTO_REJEITADO;
  }

  /** Avaliação mais recente que rejeitou o documento (motivo, quando e por quem). */
  rejeicao(documento: InstitutoPrevidenciarioDocumento): InstitutoPrevidenciarioAvaliacao | undefined {
    return [...documento.avaliacoes]
      .filter((avaliacao) => avaliacao.resultado === DOCUMENTO_REJEITADO)
      .sort((a, b) => b.avaliadoEm.localeCompare(a.avaliadoEm))[0];
  }

  voltar(): void {
    void this.router.navigate(['/requerimento-online/solicitacoes']);
  }

  // ── visualização do documento ───────────────────────────────────────────────

  abrirVisualizacao(documento: InstitutoPrevidenciarioDocumento): void {
    this.documentoVisualizado.set(documento);
  }

  fecharVisualizacao(): void {
    this.documentoVisualizado.set(null);
  }

  // ── aprovação ───────────────────────────────────────────────────────────────

  abrirConfirmacao(): void {
    this.confirmacaoAberta.set(true);
  }

  fecharConfirmacao(): void {
    this.confirmacaoAberta.set(false);
  }

  /** Ainda sem API de aprovação: só confirma na tela. */
  aprovar(): void {
    this.confirmacaoAberta.set(false);
    this.toastService.show(this.documentoUnico() ? 'Documento aprovado.' : 'Documentos aprovados.', 'success');
  }

  // ── pedido de reenvio ───────────────────────────────────────────────────────

  /** Com um documento só, ele já vem marcado: não há o que escolher. */
  abrirReenvio(): void {
    const documentos = this.documentos();
    this.reenvioForm.reset({ documentos: this.documentoUnico() ? [documentos[0].nomeArquivo] : [] });
    this.reenvioAberto.set(true);
  }

  fecharReenvio(): void {
    this.reenvioAberto.set(false);
  }

  documentoSelecionado(nomeArquivo: string): boolean {
    return this.reenvioForm.controls.documentos.value.includes(nomeArquivo);
  }

  alternarDocumento(nomeArquivo: string, marcado: boolean): void {
    const controle = this.reenvioForm.controls.documentos;
    const outros = controle.value.filter((nome) => nome !== nomeArquivo);
    controle.setValue(marcado ? [...outros, nomeArquivo] : outros);
    controle.markAsTouched();
  }

  /** Mensagem de erro do comentário, só depois que o campo foi tocado. */
  erroComentario(): string | null {
    const controle = this.reenvioForm.controls.comentarios;
    if (!controle.touched || controle.valid) {
      return null;
    }
    return `O comentário deve ter no mínimo ${COMENTARIO_MINIMO} caracteres.`;
  }

  /** Ainda sem API de reenvio: valida o formulário e só confirma na tela. */
  enviarReenvio(): void {
    if (this.reenvioForm.invalid) {
      this.reenvioForm.markAllAsTouched();
      return;
    }
    this.reenvioAberto.set(false);
    this.toastService.show('Pedido de reenvio enviado ao participante.', 'success');
  }

  private carregarSolicitacao(id: string): void {
    this.loaderService.showLoader();

    this.requerimentoOnlineSolicitacaoService
      .obterSolicitacao(id)
      .pipe(
        take(1),
        finalize(() => this.loaderService.hideLoader()),
      )
      .subscribe({
        next: (solicitacao) => this.solicitacaoSignal.set(solicitacao),
        error: () => {
          this.naoEncontrada.set(true);
          this.toastService.show('Não foi possível carregar a solicitação.', 'error');
        },
      });
  }
}
